import { IMUItem, IRestaurantMealPeriod } from '../types/brandMenu';
import { UnavailableItemsByName } from '../redux/features/menuUnification/menuUnification.props';
import { TopLevelMenuItem } from './menu';
import { GenericMap } from './types';
import {
  INFO_SLASH_COMMAND_CATEGORY,
  InfoSlashCommandOptions,
} from './infoSlashCommands.options';
import {
  checkMealPeriodAvailabilityAndActive,
  isBMItemInformationalOnly,
  populateItemAvailability,
} from './menuUnification.utils';

export { INFO_SLASH_COMMAND_CATEGORY };

/**
 * HITL-1749: `/info_*` slash commands generated from the brand menu's
 * curated answers. Items marked `informational_only: yes` carry one
 * `topic_key: answer text` per description line (`menu category*`,
 * `restaurant info*`, `promotions*`) — the same lines gpt-context answers
 * customer questions from. Each answer becomes a top-level slash item that
 * speaks the text through the existing slash-command send path.
 */

export const INFO_SLASH_COMMAND_PREFIX = '/info_';

// Whole items that carry the marker but are not Q&A: AI prompt rules,
// escalation config, and the coupon carriers Menu Module reads (`offer #540`,
// and Wienerschnitzel / Hamburger Stand's `tf offer #286`).
const EXCLUDED_ITEM_RE =
  /^(brand[_ ]specific[_ ]rules|escalation_level_config|coupon offer|(tf )?offer #.*)$/i;

// Marker / config keys, never answers.
const EXCLUDED_KEYS = new Set([
  'informational_only',
  'skip_from_prompt',
  'information_labels',
  'meal_period',
  'skip_total_if_present',
  'is_deal',
  'is_menu_category',
  'make_top_level',
  'exclude_constraints_from_prompt',
  'category_question',
]);

// Conversation replies for the AI, not menu answers.
const CONVERSATION_KEYS = new Set([
  'salutation_greeting',
  'wellbeing_greeting',
  'audio_check',
  'ai_assistant',
  'clarification_needed',
  'ai_identity_info',
]);

const ANSWER_KEY_RE = /^[a-z0-9_]+$/;

// Key suffix -> facet, longest first so `_size_options` wins over `_options`.
const FACETS: Array<[string, string]> = [
  ['_size_options', 'sizes'],
  ['_sizes_info', 'sizes'],
  ['_size_info', 'sizes'],
  ['_sizes', 'sizes'],
  ['_size', 'sizes'],
  ['_options_info', 'options'],
  ['_option_info', 'options'],
  ['_options', 'options'],
  ['_flavors_info', 'flavors'],
  ['_flavor_info', 'flavors'],
  ['_flavors', 'flavors'],
  ['_price', 'price'],
  ['_hours_info', 'hours'],
  ['_hours', 'hours'],
  ['_info', ''],
];

const PROMOTIONS = 'promotions';

const toSnake = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const splitFacet = (key: string): { subject: string; facet: string } => {
  const snake = toSnake(key);
  for (const [suffix, facet] of FACETS) {
    if (snake.endsWith(suffix) && snake.length > suffix.length) {
      return { subject: snake.slice(0, -suffix.length), facet };
    }
  }
  return { subject: snake, facet: '' };
};

/** `/info_<subject>[_<facet>]` for a menu key, keeping the menu's wording. */
export const toInfoCommandName = (key: string): string => {
  const { subject, facet } = splitFacet(key);
  return `${INFO_SLASH_COMMAND_PREFIX}${subject}${facet ? `_${facet}` : ''}`;
};

// Whether a key ends in one of the answer suffixes above: `/basket_options`
// answers a question, a bare `/sauce` or `/size` prompt asks one.
const hasAnswerSuffix = (key: string) => {
  const snake = toSnake(key);
  return FACETS.some(
    ([suffix]) => snake.endsWith(suffix) && snake.length > suffix.length
  );
};

const singular = (word: string) => {
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (/(ch|sh|ss)es$/.test(word)) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith('s') && !/(ss|us|is)$/.test(word))
    return word.slice(0, -1);
  return word;
};

/**
 * Identity used only to spot duplicates: `drink_info` and `drinks_info`, or a
 * curated `/drink_options:` command and the generated `/info_drink_options`.
 */
export const toDuplicateKey = (key: string): string => {
  const { subject, facet } = splitFacet(key.replace(/^\/(info_)?/, ''));
  return [...subject.split('_').map(singular), facet].filter(Boolean).join('_');
};

type AnswerLine = { key: string; answer: string };

const parseAnswerLines = (description: string): AnswerLine[] =>
  description.split('\n').reduce<AnswerLine[]>((lines, line) => {
    const separator = line.indexOf(':');
    if (separator < 0) return lines;
    const key = line.slice(0, separator).trim();
    const answer = line.slice(separator + 1).trim();
    if (
      answer &&
      ANSWER_KEY_RE.test(key) &&
      !EXCLUDED_KEYS.has(key) &&
      !key.startsWith('prompt_rule_') &&
      !CONVERSATION_KEYS.has(key)
    ) {
      lines.push({ key, answer });
    }
    return lines;
  }, []);

const readLine = (description: string, key: string) =>
  description.match(new RegExp(`^\\s*${key}\\s*:(.*)$`, 'im'))?.[1]?.trim();

/**
 * Which copy wins when two live items answer the same command: a store-group
 * `menu category <x>` beats the brand-wide `menu category` / `menu category
 * all`, then `promotions`, `restaurant info`, and last the old-style `*_info`
 * category items. Lower wins.
 */
export const getSourcePrecedence = (itemName: string): number => {
  const name = itemName.trim().toLowerCase();
  if (name.startsWith('menu category')) {
    return name === 'menu category' || name === 'menu category all' ? 1 : 0;
  }
  if (name.startsWith(PROMOTIONS)) return 2;
  if (name.startsWith('restaurant info')) return 3;
  return 4;
};

// `promotions taco tuesday crispy` -> `taco_tuesday_crispy`; '' for `promotions`.
const promotionName = (itemName: string) =>
  toSnake(itemName.trim().slice(PROMOTIONS.length));

const isPromotionItem = (itemName: string) =>
  itemName.trim().toLowerCase().startsWith(PROMOTIONS);

type Candidate = {
  name: string;
  duplicateKey: string;
  answer: string;
  synonyms: string[];
  precedence: number;
  order: number;
};

export type BuildInfoSlashCommandsArgs = {
  brandMenuItems: IMUItem[];
  existingItemNames: string[];
  unavailableItemsByName: UnavailableItemsByName;
  exclude: string[];
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
};

/**
 * The `/info_*` items live at this store right now. Only items whose meal
 * period is active here contribute — the per-store meal periods already carry
 * the restaurant-group scoping — so a rebuild on daypart rollover picks the
 * copy that applies at that moment.
 */
export const buildInfoSlashCommands = ({
  brandMenuItems,
  existingItemNames,
  unavailableItemsByName,
  exclude,
  timezone,
  restaurantMealPeriodsByName,
}: BuildInfoSlashCommandsArgs): GenericMap<TopLevelMenuItem> => {
  const excluded = new Set(exclude.map((key) => key.trim().toLowerCase()));
  // Only an existing command that is itself an answer covers a topic; a bare
  // clarification prompt (Carl's Jr `/sauce`, DQ `/size`) must not hide
  // `sauce_info` / `sizes_info`.
  const existing = new Set(
    existingItemNames
      .map((name) => name.split(':')[0])
      .filter((name) => name.startsWith('/') && hasAnswerSuffix(name))
      .map(toDuplicateKey)
  );
  const sourceItems = brandMenuItems.filter(
    (item) =>
      isBMItemInformationalOnly(item) && !EXCLUDED_ITEM_RE.test(item.name)
  );

  const mealPeriodActiveCache = new Map<string, boolean>();
  // Only items live at this store right now: unavailable or out-of-period
  // copies neither answer nor count towards the promotion split below.
  const liveItems = sourceItems.filter(
    (item) =>
      populateItemAvailability(unavailableItemsByName[item.name]).available &&
      checkMealPeriodAvailabilityAndActive({
        mealPeriod: item.meal_period,
        timezone,
        restaurantMealPeriodsByName,
        entityName: item.name,
        mealPeriodActiveCache,
      })
  );

  // A key several promotions live at the same time define (Taco John's
  // `current_offers`) is one command per promotion, so the agent picks the
  // offer that fits instead of hearing every live offer back to back. Counted
  // over live items only: DQ's `promotions canada sussex` / `canada kamloops`
  // / `grill n chill` are store-group copies of the same keys, one live per
  // store, and must keep their plain names (`/info_loyalty_program`).
  const promotionKeyCounts = new Map<string, number>();
  liveItems
    .filter((item) => isPromotionItem(item.name) && promotionName(item.name))
    .forEach((item) =>
      new Set(parseAnswerLines(item.description).map(({ key }) => key)).forEach(
        (key) =>
          promotionKeyCounts.set(key, (promotionKeyCounts.get(key) ?? 0) + 1)
      )
    );
  const isSharedPromotionKey = (key: string) =>
    (promotionKeyCounts.get(key) ?? 0) > 1;

  const winners = new Map<string, Candidate>();
  let order = 0;

  liveItems.forEach((item) => {
    const lines = parseAnswerLines(item.description);
    // An old-style `<topic>_info` item answers with its category_question.
    const categoryQuestion = readLine(item.description, 'category_question');
    if (categoryQuestion)
      lines.push({ key: item.name, answer: categoryQuestion });
    const labels = (readLine(item.description, 'information_labels') ?? '')
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean);

    const promotion = isPromotionItem(item.name)
      ? promotionName(item.name)
      : '';
    // A promotion with one shared key is `/info_offer_<promo>`; one with
    // several also names the key, so no line overwrites another.
    const sharedKeyCount = promotion
      ? new Set(lines.map(({ key }) => key).filter(isSharedPromotionKey)).size
      : 0;

    lines.forEach(({ key, answer }) => {
      const isPerPromotion = !!promotion && isSharedPromotionKey(key);
      const offer =
        sharedKeyCount > 1
          ? `offer_${promotion}_${toInfoCommandName(key).slice(
              INFO_SLASH_COMMAND_PREFIX.length
            )}`
          : `offer_${promotion}`;
      const name = isPerPromotion
        ? `${INFO_SLASH_COMMAND_PREFIX}${offer}`
        : toInfoCommandName(key);
      const duplicateKey = isPerPromotion ? offer : toDuplicateKey(key);
      // By source key, or by command name for one per-promotion command.
      if (
        excluded.has(key.toLowerCase()) ||
        excluded.has(name.slice(1)) ||
        excluded.has(name)
      )
        return;
      if (existing.has(duplicateKey)) return;

      const candidate: Candidate = {
        name,
        duplicateKey,
        answer,
        synonyms: Array.from(
          new Set([
            key.replace(/_/g, ' '),
            ...(isPerPromotion ? [promotion.replace(/_/g, ' ')] : []),
            ...(key === item.name ? labels : []),
          ])
        ),
        precedence: getSourcePrecedence(item.name),
        order: order++,
      };
      const current = winners.get(duplicateKey);
      // `<=` so a later line of an equally ranked source wins, as gpt-context
      // keeps the last line when one item repeats a key.
      if (!current || candidate.precedence <= current.precedence) {
        winners.set(duplicateKey, candidate);
      }
    });
  });

  const infoItems: GenericMap<TopLevelMenuItem> = {};
  Array.from(winners.values())
    .sort((a, b) => a.order - b.order)
    .forEach(({ name, answer, synonyms }) => {
      infoItems[name] = {
        id: name,
        name,
        category: INFO_SLASH_COMMAND_CATEGORY,
        categoryId: INFO_SLASH_COMMAND_CATEGORY,
        available: true,
        synonyms,
        // One phrase: splitPromptPhrases would cut a `;` in the answer into
        // separate asks.
        slashCommands: [answer],
        slashCommandIdx: 0,
      };
    });
  return infoItems;
};

/**
 * The merged item list: the generated commands never replace an item the
 * menu already has.
 */
export const withInfoSlashCommands = (
  topLevelMenuItems: GenericMap<TopLevelMenuItem>,
  options: InfoSlashCommandOptions,
  args: Omit<BuildInfoSlashCommandsArgs, 'existingItemNames' | 'exclude'>
): GenericMap<TopLevelMenuItem> => {
  if (!options) return topLevelMenuItems;
  const infoItems = buildInfoSlashCommands({
    ...args,
    existingItemNames: Object.keys(topLevelMenuItems),
    exclude: options.exclude,
  });
  return { ...infoItems, ...topLevelMenuItems };
};
